import { Dispatch, SetStateAction, useState } from "react";
import { AddressManager } from "@/common/components/addresses";
import { KeyValue } from "@/common/components/key-value";
import { Button } from "@/common/components/button";
import Form from "@/common/components/form/Form";
import { MultiSelect } from "@/common/components/multi-selectInput";
import { KeyValueObj } from "@/common/types";
import { pluralize } from "@/common/utils/general";
import { readBatchUnit } from "@/common/utils/quantity";
import { ProductOrderContentWrapper, StatusPill } from "./style";
import {
  productStatusConfig,
  getProductOrderStats,
  productOrderFormInputs,
} from "./data";

export interface ISelectedProduct {
  id: string;
  name: string;
  company: string;
  batchNo: string;
  expiryDate: string;
  /**
   * MED-697: the stock reading, already rendered by the server at the level
   * THIS holder sells at and summed per batch — "3 Cartons", "8 Packs + 3
   * Tablets". A string rather than a count because the remainder below the
   * selling level has no place in a single number.
   *
   * Declared but not read in this component: the order form counts in
   * `item.quantity` per batch instead, which is the right granularity for a
   * per-batch input. Kept because the card that opens this modal renders it.
   */
  available: string;
  imageUrl: string;
  status: "available" | "out-of-stock" | "low-stock";
  // MED-706: card-level summary — `price` and `packagingType` come from the
  // most recently listed priced batch, read at that batch's own selling level
  // (all-products-order.tsx). The per-batch picker below reads each selected
  // batch's own level instead; this pair is only the headline "Price" stat.
  price: string;
  packagingType: string;
}

// One batch the buyer has picked, with the quantity they want from it — all
// at THIS batch's own selling level (MED-706), which for a manufacturer is the
// product's highest layer but for an importer/distributor may be lower.
// Mirrors make-order/subs/ReqForm.tsx's per-batch selection shape.
export interface ISelectedBatch {
  id: string;
  title: string;
  amount: number;
  quantity: number;
  /** Base units one selling-level package of this batch holds (MED-706). */
  multiplier: number;
  /** This batch's own selling-level label, e.g. "Carton" / "Pack". */
  unitLabel: string;
  sellingPrice: number;
  discountPercentage: number | null;
  originalPrice: number | null;
}

export interface IProductOrderContentProps {
  product: ISelectedProduct | null;
  // Raw per-batch records from the API (DrugSerializer#batches) for the
  // selected product. Each carries its own `quantity_display` (MED-697), which
  // is where the batch picker reads the selling level, base-unit multiplier and
  // in-stock ceiling from — not a product-level count.
  batches: KeyValueObj[];
  selectedBatches: ISelectedBatch[];
  setSelectedBatches: Dispatch<SetStateAction<ISelectedBatch[]>>;
  selectedAddress: KeyValueObj;
  setSelectedAddress: Dispatch<SetStateAction<KeyValueObj>>;
  expectedDeliveryDate: string;
  setExpectedDeliveryDate: Dispatch<SetStateAction<string>>;
  orderNote: string;
  setOrderNote: Dispatch<SetStateAction<string>>;
  submittingOrder: boolean;
  onSubmit: () => void;
}

// Buyer-facing "quick order" panel opened from a product card in the "all
// products" catalogue tab. Pure content, no Modal import — the caller
// (orders/index.tsx) wraps this in <Modal>, same convention as ActionEnquiry.
// Styled to match make-order/subs/ReqForm.tsx's ReqFormWrapper (see style.ts)
// since both hit ORDERS_URL to create an order, and reuses the same batch
// picker shape (MultiSelect + per-batch quantity) so a buyer can choose
// which batch(es) to draw from instead of one flat quantity applied to
// every batch in stock.
export function ProductOrderContent({
  product,
  batches,
  selectedBatches,
  setSelectedBatches,
  selectedAddress,
  setSelectedAddress,
  expectedDeliveryDate,
  setExpectedDeliveryDate,
  setOrderNote,
  submittingOrder,
  onSubmit,
}: IProductOrderContentProps) {
  const [formValues, setFormValues] = useState({});

  if (!product) return null;

  const stock = productStatusConfig[product.status];

  // selling_price_per_unit is already discount-applied (see DrugSerializer#
  // batches -> PricingManager::UnitConverter.apply_discount on the backend).
  // originalPrice is derived back out for display only.
  const batchOptions = (batches ?? []).map((batch: any) => {
    // MED-706: this batch's own selling level, from its `quantity_display` —
    // one product's batches can belong to holders selling at different levels.
    const unit = readBatchUnit(batch.quantity_display);
    const sellingPrice = Number(batch.selling_price_per_unit) * unit.multiplier;
    const discountPercentage = batch.discount_percentage ?? null;
    const originalPrice = discountPercentage
      ? sellingPrice / (1 - discountPercentage / 100)
      : null;

    return {
      id: batch.inventory_id,
      // MultiSelect renders `title` as plain text in both the dropdown list
      // and the selected chips — bake the discount in here.
      title: discountPercentage
        ? `${batch.batch_number} (${discountPercentage}% OFF)`
        : batch.batch_number,
      quantity: unit.inStock,
      multiplier: unit.multiplier,
      unitLabel: unit.label,
      sellingPrice,
      discountPercentage,
      originalPrice,
    };
  });

  const hasInvalidBatch = selectedBatches.some(
    (batch) => !(batch.amount > 0) || batch.amount > batch.quantity,
  );

  return (
    <ProductOrderContentWrapper>
      <div className="header">
        <img src={product.imageUrl} alt={product.name} />
        <div className="info">
          <div>
            <h2>{product.name}</h2>
            <p className="company">{product.company}</p>
          </div>
          <StatusPill color={stock.color}>
            <span className="status-dot" />
            <span>{stock.label}</span>
          </StatusPill>
        </div>
      </div>

      <div className="stats">
        {getProductOrderStats(product).map(({ label, value }) => (
          <KeyValue key={label} title={label} value={value} />
        ))}
      </div>

      <div className="line" />

      <div className="section">
        <p className="section-title">Select Batches</p>
        <MultiSelect
          fullWidth
          name="batches"
          prompt="Select Product Batches"
          placeholder="Choose batches"
          options={batchOptions}
          returnId
          formValues={formValues}
          setFormValues={setFormValues}
          onEveryChange={(selectedIds) => {
            const existingAmounts = Object.fromEntries(
              selectedBatches.map((item) => [item.id, item.amount]),
            );

            setSelectedBatches(
              batchOptions
                .filter((batch) => selectedIds.includes(batch.id))
                .map((batch) => ({
                  id: batch.id,
                  title: batch.title,
                  amount: existingAmounts[batch.id] || 0,
                  quantity: batch.quantity,
                  multiplier: batch.multiplier,
                  unitLabel: batch.unitLabel,
                  sellingPrice: batch.sellingPrice,
                  discountPercentage: batch.discountPercentage,
                  originalPrice: batch.originalPrice,
                })),
            );
          }}
        />

        <div className="batch-list">
          {selectedBatches.map((item) => (
            <div
              key={item.id}
              className={`batch-card ${item.amount > item.quantity ? "invalid" : ""}`}
            >
              <div className="batch-info">
                <span className="batch-title">{item.title}</span>
                <span className="batch-meta">
                  In stock: {pluralize(item.quantity, item.unitLabel)}{" "}
                  · Price:{" "}
                  {item.discountPercentage ? (
                    <>
                      <span className="strike">
                        ₦{Number(item.originalPrice).toLocaleString()}
                      </span>{" "}
                      <span className="discounted">
                        ₦{Number(item.sellingPrice).toLocaleString()}
                      </span>
                    </>
                  ) : (
                    <>₦{Number(item.sellingPrice).toLocaleString()}</>
                  )}{" "}
                  / {item.unitLabel}
                </span>
                {item.amount > item.quantity && (
                  <span className="batch-error">
                    Only {pluralize(item.quantity, item.unitLabel)}{" "}
                    available in this batch
                  </span>
                )}
              </div>
              <input
                type="number"
                min={0}
                max={item.quantity}
                placeholder="Qty"
                value={item.amount || ""}
                onChange={(e) => {
                  const amount = Number(e.target.value);
                  setSelectedBatches((prev) =>
                    prev.map((batch) =>
                      batch.id === item.id ? { ...batch, amount } : batch,
                    ),
                  );
                }}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="section">
        <p className="section-title">Delivery Address</p>
        <AddressManager
          selectedAddress={selectedAddress}
          setSelectedAddress={setSelectedAddress}
          hideDelete
        />
      </div>

      <div className="section">
        <Form
          formInputs={productOrderFormInputs}
          processInputs={() => {}}
          removeSubmitButton
          removePadding
          externalUpdateData={(name, value) => {
            if (name === "expectedDeliveryDate")
              setExpectedDeliveryDate(value);
            if (name === "orderNote") setOrderNote(value);
          }}
        />
      </div>

      <Button
        disabled={
          stock.disabled ||
          submittingOrder ||
          selectedBatches.length === 0 ||
          hasInvalidBatch ||
          !expectedDeliveryDate ||
          !selectedAddress?.id
        }
        loading={submittingOrder}
        onClick={onSubmit}
        bg="#33145f"
        width="100%"
      >
        Proceed to Order
      </Button>
    </ProductOrderContentWrapper>
  );
}
